/**
 * As três peças de interface que um PWA precisa: convite para instalar, aviso de
 * versão nova e indicador de que a conexão caiu.
 *
 * Todas discretas de propósito. Um banner de instalação que ocupa meia tela é o
 * tipo de coisa que faz o usuário fechar o app em vez de instalá-lo.
 */

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { CloudOff, Download, RefreshCw, Share, X } from 'lucide-react';
import { Button } from '@/components/ui';
import { cn } from '@/lib/cn';
import { APK_URL, ehAndroidNoNavegador, useInstalacao, useOnline } from '@/lib/pwa';
import { useLocalStorage } from '@/lib/hooks';

// ---------------------------------------------------------------------------

/**
 * Convite para instalar, no rodapé.
 *
 * No Android o convite é para o APK, não para o PWA: os dois têm as mesmas telas,
 * mas só o app nativo puxa a sessão do MyChron pelo WiFi.
 */
export function ConviteDeInstalacao() {
  return ehAndroidNoNavegador() ? <ConviteDoApk /> : <ConviteDoPwa />;
}

/**
 * Link de download do APK com cara de botão.
 *
 * Um `<a download>`, não um `<button>`: é um arquivo, e o navegador do Android só
 * oferece "abrir/instalar" no fim do download quando ele chega por um link.
 */
export function LinkDoApk({
  variant = 'primary',
  size = 'sm',
  children = 'Baixar',
}: {
  variant?: 'primary' | 'secondary';
  size?: 'sm' | 'md';
  children?: ReactNode;
}) {
  return (
    <a
      href={APK_URL}
      download="MychronoHelper.apk"
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center border font-semibold',
        'transition-colors duration-150 ease-out-soft',
        variant === 'primary'
          ? 'border-primary bg-primary text-on-primary hover:border-primary-hover hover:bg-primary-hover'
          : 'border-border bg-surface-2 text-text hover:border-border-strong hover:bg-surface-3',
        size === 'sm' ? 'h-9 gap-1.5 rounded-sm px-3 text-sm' : 'h-11 gap-2 rounded-md px-4 text-base',
      )}
    >
      <Download aria-hidden="true" className={cn('shrink-0', size === 'sm' ? 'size-4' : 'size-[18px]')} />
      {children}
    </a>
  );
}

/**
 * Some por 7 dias se dispensado — menos que o do PWA porque, no Android, é o
 * único caminho para o WiFi do MyChron.
 */
function ConviteDoApk() {
  const [dispensadoEm, setDispensadoEm] = useLocalStorage<number>('apk-dispensado', 0);
  if (Date.now() - dispensadoEm < 7 * 24 * 60 * 60 * 1000) return null;

  return (
    <div
      className="fixed inset-x-0 z-40 px-3"
      style={{ bottom: 'calc(var(--height-bottom-nav) + env(safe-area-inset-bottom) + 8px)' }}
    >
      <div className="mx-auto flex max-w-2xl items-center gap-3 rounded-lg border border-border bg-surface-2 px-3 py-2.5 shadow-overlay">
        <img src="/icons/icon-192.png" alt="" aria-hidden className="h-9 w-9 shrink-0 rounded-md" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">App do MychronoHelper para Android</p>
          <p className="text-xs text-text-muted">Puxa a sessão direto do MyChron pelo WiFi.</p>
        </div>
        <LinkDoApk />
        <Button
          variant="ghost"
          size="sm"
          icon={X}
          iconOnly
          aria-label="Agora não"
          onClick={() => setDispensadoEm(Date.now())}
        />
      </div>
    </div>
  );
}

/**
 * Só aparece depois de o navegador confirmar que a instalação é possível, e
 * some por 30 dias se for dispensado — insistir a cada visita é o que faz as
 * pessoas ignorarem o banner para sempre.
 */
function ConviteDoPwa() {
  const { podeInstalar, precisaInstrucoesIos, instalar } = useInstalacao();
  const [dispensadoEm, setDispensadoEm] = useLocalStorage<number>('pwa-dispensado', 0);
  const [mostrarIos, setMostrarIos] = useState(false);

  const dispensadoRecentemente = Date.now() - dispensadoEm < 30 * 24 * 60 * 60 * 1000;
  if (dispensadoRecentemente) return null;
  if (!podeInstalar && !precisaInstrucoesIos) return null;

  return (
    <>
      <div
        className="fixed inset-x-0 z-40 px-3"
        style={{ bottom: 'calc(var(--height-bottom-nav) + env(safe-area-inset-bottom) + 8px)' }}
      >
        <div className="mx-auto flex max-w-2xl items-center gap-3 rounded-lg border border-border bg-surface-2 px-3 py-2.5 shadow-overlay">
          <img src="/icons/icon-192.png" alt="" aria-hidden className="h-9 w-9 shrink-0 rounded-md" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">Instalar o MychronoHelper</p>
            <p className="text-xs text-text-muted">
              Abre em tela cheia e funciona sem internet no box.
            </p>
          </div>
          <Button
            variant="primary"
            size="sm"
            icon={precisaInstrucoesIos ? Share : Download}
            onClick={() => (precisaInstrucoesIos ? setMostrarIos(true) : void instalar())}
          >
            Instalar
          </Button>
          <Button
            variant="ghost"
            size="sm"
            icon={X}
            iconOnly
            aria-label="Agora não"
            onClick={() => setDispensadoEm(Date.now())}
          />
        </div>
      </div>

      {mostrarIos && <InstrucoesIos onFechar={() => setMostrarIos(false)} />}
    </>
  );
}

/**
 * O iOS não expõe API de instalação — o caminho é o menu Compartilhar. Sem estas
 * instruções o botão simplesmente não faria nada no iPhone.
 */
function InstrucoesIos({ onFechar }: { onFechar: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-3"
      role="dialog"
      aria-modal="true"
      aria-label="Como instalar no iPhone"
      onClick={onFechar}
    >
      <div
        className="w-full max-w-md rounded-lg border border-border bg-surface p-4"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="text-lg">Instalar no iPhone</h2>
        <ol className="mt-3 space-y-2.5 text-sm text-text-muted">
          <li className="flex gap-2.5">
            <span className="num shrink-0 font-semibold text-text">1.</span>
            <span>
              Toque em <Share size={14} className="inline align-text-bottom" aria-label="Compartilhar" />{' '}
              <strong className="font-medium text-text">Compartilhar</strong>, na barra do Safari.
            </span>
          </li>
          <li className="flex gap-2.5">
            <span className="num shrink-0 font-semibold text-text">2.</span>
            <span>
              Role e escolha{' '}
              <strong className="font-medium text-text">Adicionar à Tela de Início</strong>.
            </span>
          </li>
          <li className="flex gap-2.5">
            <span className="num shrink-0 font-semibold text-text">3.</span>
            <span>
              Confirme em <strong className="font-medium text-text">Adicionar</strong>.
            </span>
          </li>
        </ol>
        <p className="mt-3 text-xs text-text-faint">
          Precisa ser pelo Safari — no Chrome do iPhone essa opção não aparece.
        </p>
        <div className="mt-4 flex justify-end">
          <Button variant="secondary" onClick={onFechar}>
            Entendi
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Aviso de versão nova.
 *
 * A atualização não é aplicada sozinha de propósito: recarregar a página no meio
 * de uma análise perderia a volta que a pessoa estava olhando.
 */
export function AvisoDeAtualizacao({ aplicar }: { aplicar: (() => void) | null }) {
  if (!aplicar) return null;

  return (
    <div className="fixed inset-x-0 top-0 z-50 px-3 pt-2 safe-top">
      <div className="mx-auto flex max-w-2xl items-center gap-3 rounded-lg border border-you/40 bg-surface-2 px-3 py-2 shadow-overlay">
        <RefreshCw size={16} className="shrink-0 text-you" aria-hidden />
        <p className="min-w-0 flex-1 text-sm">Tem uma versão nova do app.</p>
        <Button variant="secondary" size="sm" onClick={aplicar}>
          Atualizar
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Faixa de offline.
 *
 * Diz o que ainda funciona, não só que caiu: o app guarda o que já foi aberto, e
 * saber disso é a diferença entre esperar o sinal voltar e continuar analisando.
 */
export function FaixaOffline() {
  const online = useOnline();
  const [visivel, setVisivel] = useState(false);

  // Pequeno atraso para não piscar em oscilação de meio segundo, que é comum
  // justamente no lugar onde este app é usado.
  useEffect(() => {
    if (!online) {
      const id = window.setTimeout(() => setVisivel(true), 1200);
      return () => window.clearTimeout(id);
    }
    setVisivel(false);
    return undefined;
  }, [online]);

  if (!visivel) return null;

  return (
    <div
      className="sticky top-0 z-30 flex items-center justify-center gap-2 bg-warning/15 px-3 py-1.5 text-xs text-warning"
      role="status"
      aria-live="polite"
    >
      <CloudOff size={13} aria-hidden />
      <span>Sem conexão — dá para ver o que já foi aberto antes; importar e publicar precisam de rede.</span>
    </div>
  );
}
